import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Experiment } from "../lib/api";
import { useSession } from "../session";
import {
  Badge,
  Card,
  Empty,
  ErrorBox,
  LoadingDashboard,
  MetricCard,
  PageHeader,
  Paywall,
  Why,
} from "../components/ui";
import { IconFlask, IconPlus } from "../components/icons";
import { auto, decimal, num, rangeLabel, signedPct } from "../lib/format";

interface ListResponse {
  experiments: Experiment[];
  variables: Record<string, string>;
  metrics: Record<string, string>;
}

interface Verdict {
  decision: string;
  headline: string;
  n_a: number;
  n_b: number;
  median_a: number | null;
  median_b: number | null;
  delta_pct: number | null;
  effect: number | null;
  p_value: number | null;
  ci_low: number | null;
  ci_high: number | null;
  strata_used: number;
  notes: string[];
}

const STATUS_TITLE: Record<string, string> = {
  draft: "черновик",
  proposed: "предложен",
  running: "идёт",
  done: "завершён",
  cancelled: "отменён",
};

export default function ExperimentsPage() {
  const { channelId, can } = useSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ["experiments", channelId],
    queryFn: () => api.get<ListResponse>(`/experiments/${channelId}`),
    enabled: Boolean(channelId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["experiments", channelId] });

  const approve = useMutation({
    mutationFn: (id: number) => api.post(`/experiments/${channelId}/${id}/approve`),
    onSuccess: invalidate,
  });
  const finish = useMutation({
    mutationFn: (id: number) => api.post(`/experiments/${channelId}/${id}/finish`),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: (id: number) => api.post(`/experiments/${channelId}/${id}/cancel`),
    onSuccess: invalidate,
  });

  if (!channelId) return <Empty title="Сначала подключите канал" />;
  if (list.error instanceof ApiError && list.error.isPaywall) return <Paywall what="Эксперименты" />;
  if (list.isLoading) return <LoadingDashboard />;
  if (list.error) return <ErrorBox error={list.error} />;
  const data = list.data!;

  return (
    <>
      <PageHeader
        eyebrow="Детальная аналитика"
        title="Эксперименты"
        lede="Слоты расписания заранее раскладываются по вариантам блоками, а не случайно: на двадцати публикациях случай легко даёт «все A утром», и тогда эксперимент измерит время суток, а не то, что проверяли."
        actions={
          can("write_experiments") && (
            <button className="primary" onClick={() => setCreating((value) => !value)}>
              {!creating && <IconPlus size={15} />}
              {creating ? "Отмена" : "Новый эксперимент"}
            </button>
          )
        }
      />

      {creating && (
        <ExperimentForm
          channelId={channelId}
          variables={data.variables}
          metrics={data.metrics}
          onDone={() => {
            setCreating(false);
            invalidate();
          }}
        />
      )}

      {data.experiments.length === 0 ? (
        <Card>
          <Empty title="Экспериментов пока нет" icon={<IconFlask size={22} />}>
            Проще всего начать с раздела «Возможности»: у подходящих выводов есть кнопка «Проверить
            гипотезу», которая заводит эксперимент с готовым планом.
          </Empty>
        </Card>
      ) : (
        data.experiments.map((experiment) => (
          <ExperimentCard
            key={experiment.id}
            experiment={experiment}
            channelId={channelId}
            onApprove={() => approve.mutate(experiment.id)}
            onFinish={() => finish.mutate(experiment.id)}
            onCancel={() => cancel.mutate(experiment.id)}
            canWrite={can("write_experiments")}
          />
        ))
      )}
    </>
  );
}

function ExperimentCard({
  experiment,
  channelId,
  onApprove,
  onFinish,
  onCancel,
  canWrite,
}: {
  experiment: Experiment;
  channelId: number;
  onApprove: () => void;
  onFinish: () => void;
  onCancel: () => void;
  canWrite: boolean;
}) {
  const verdict = useQuery({
    queryKey: ["experiment-result", channelId, experiment.id],
    queryFn: () =>
      api.get<{ verdict: Verdict }>(`/experiments/${channelId}/${experiment.id}/result`),
    enabled: experiment.status === "running" || experiment.status === "done",
  });

  const result = (experiment.result as unknown as Verdict) ?? verdict.data?.verdict;

  return (
    <Card
      title={experiment.name}
      note={`${experiment.variant_a} против ${experiment.variant_b} · ${experiment.primary_metric_title}`}
    >
      <div className="row" style={{ marginBottom: 10 }}>
        <Badge tone={experiment.status === "running" ? "accent" : experiment.status === "done" ? "ok" : "muted"}>
          {STATUS_TITLE[experiment.status] ?? experiment.status}
        </Badge>
        {experiment.mode === "observational" && <Badge tone="warn">наблюдательный режим</Badge>}
        {experiment.started_at && (
          <span className="faint">{rangeLabel(experiment.started_at, experiment.ends_at)}</span>
        )}
        <span className="faint">
          слотов {num(experiment.assignments.total)} · опубликовано A{" "}
          {num(experiment.assignments.published_by_variant.A ?? 0)} / B{" "}
          {num(experiment.assignments.published_by_variant.B ?? 0)}
        </span>
      </div>

      {experiment.hypothesis && <p className="muted">{experiment.hypothesis}</p>}

      {result && (
        <>
          {/* Итог — единственное крупное число карточки: ради него эксперимент
              и заводился. Остальное уезжает в «как это посчитано». */}
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "var(--s3)" }}>
            <MetricCard small name="Медиана варианта A" value={auto(result.median_a)} context={`${num(result.n_a)} публикаций`} />
            <MetricCard small name="Медиана варианта B" value={auto(result.median_b)} context={`${num(result.n_b)} публикаций`} />
            <MetricCard small name="Разница" value={signedPct(result.delta_pct)} context="B относительно A" />
          </div>
          <div className="action-line">{result.headline}</div>
          <Why summary="Как это посчитано">
            <table className="facts">
              <tbody>
                <tr>
                  <td className="dim">Публикаций A / B</td>
                  <td className="mono">
                    {num(result.n_a)} / {num(result.n_b)}
                  </td>
                </tr>
                <tr>
                  <td className="dim">Медиана A / B</td>
                  <td className="mono">
                    {auto(result.median_a)} / {auto(result.median_b)}
                  </td>
                </tr>
                <tr>
                  <td className="dim">Разница</td>
                  <td className="mono">{signedPct(result.delta_pct)}</td>
                </tr>
                <tr>
                  <td className="dim">Доверительный интервал разницы</td>
                  <td className="mono">
                    {result.ci_low === null ? "—" : `${signedPct(result.ci_low)} … ${signedPct(result.ci_high)}`}
                  </td>
                </tr>
                <tr>
                  <td className="dim">Вероятность случайности (p)</td>
                  <td className="mono">{result.p_value === null ? "—" : decimal(result.p_value, 3)}</td>
                </tr>
                <tr>
                  <td className="dim">Сопоставимых групп</td>
                  <td className="mono">{num(result.strata_used)}</td>
                </tr>
              </tbody>
            </table>
            <ul style={{ marginTop: 10, paddingLeft: 18 }}>
              {result.notes?.map((note, index) => (
                <li key={index}>{note}</li>
              ))}
            </ul>
          </Why>
        </>
      )}

      {canWrite && (
        <div className="row" style={{ marginTop: 12 }}>
          {(experiment.status === "draft" || experiment.status === "proposed") && (
            <button className="primary small" onClick={onApprove}>
              Утвердить и запустить
            </button>
          )}
          {experiment.status === "running" && (
            <>
              <button className="small" onClick={onFinish}>
                Завершить и решить
              </button>
              <button className="ghost small" onClick={onCancel}>
                Отменить
              </button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function ExperimentForm({
  channelId,
  variables,
  metrics,
  onDone,
}: {
  channelId: number;
  variables: Record<string, string>;
  metrics: Record<string, string>;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: "",
    hypothesis: "",
    variable: "format",
    variant_a: "text",
    variant_b: "video",
    primary_metric: "views_24h",
    mode: "controlled",
    min_per_variant: 10,
    planned_days: 14,
  });

  const save = useMutation({
    mutationFn: () => api.post(`/experiments/${channelId}`, form),
    onSuccess: onDone,
  });

  return (
    <Card title="Новый эксперимент">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid two">
          <div className="field">
            <label>Название</label>
            <input
              required
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="field">
            <label>Что меняем</label>
            <select
              value={form.variable}
              onChange={(event) => setForm({ ...form, variable: event.target.value })}
            >
              {Object.entries(variables).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Вариант A (как сейчас)</label>
            <input
              value={form.variant_a}
              onChange={(event) => setForm({ ...form, variant_a: event.target.value })}
            />
          </div>
          <div className="field">
            <label>Вариант B (что проверяем)</label>
            <input
              value={form.variant_b}
              onChange={(event) => setForm({ ...form, variant_b: event.target.value })}
            />
          </div>
          <div className="field">
            <label>Что считаем успехом</label>
            <select
              value={form.primary_metric}
              onChange={(event) => setForm({ ...form, primary_metric: event.target.value })}
            >
              {Object.entries(metrics).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Режим</label>
            <select value={form.mode} onChange={(event) => setForm({ ...form, mode: event.target.value })}>
              <option value="controlled">Управляемый — план исполняет Tokel</option>
              <option value="observational">Наблюдательный — размечаем то, что вышло</option>
            </select>
            <span className="faint">
              В наблюдательном режиме варианты не назначались заранее, и уверенность
              вывода ниже.
            </span>
          </div>
        </div>
        <div className="field">
          <label>Гипотеза</label>
          <textarea
            rows={2}
            value={form.hypothesis}
            onChange={(event) => setForm({ ...form, hypothesis: event.target.value })}
          />
        </div>
        {save.error && <div style={{ marginBottom: 10 }}><ErrorBox error={save.error} /></div>}
        <button className="primary" type="submit" disabled={save.isPending}>
          Создать
        </button>
      </form>
    </Card>
  );
}
